"use client";

import { useState, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, Mail, Lock, Loader2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { GlassCard } from "@/components/common/GlassCard";
import { BRAND } from "@/lib/marketing/copy";
import { loginWithPassword } from "@/lib/auth/actions";
import { useAuth } from "@/contexts/AuthContext";

type OtpStep = "phone" | "verify";
type TabValue = "customer" | "staff";

// Optional API base (empty string by default in dev with Vite proxy)
const API_BASE = import.meta.env.VITE_API_BASE || "";

// SegmentedTabs: Custom tab switcher with animated highlight
function SegmentedTabs({
  value,
  onChange,
}: {
  value: TabValue;
  onChange: (val: TabValue) => void;
}) {
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
        e.preventDefault();
        onChange(value === "customer" ? "staff" : "customer");
      }
    },
    [value, onChange]
  );

  return (
    <div
      role="tablist"
      aria-label="Login type"
      tabIndex={0}
      onKeyDown={onKeyDown}
      className="relative w-full rounded-2xl bg-secondary p-1"
    >
      {/* Animated highlight pill */}
      <motion.div
        layout
        transition={{ type: "spring", stiffness: 400, damping: 30 }}
        className="absolute top-1 rounded-xl bg-white shadow-sm"
        style={{
          left: value === "customer" ? 4 : "calc(50% + 4px)",
          width: "calc(50% - 8px)",
          height: "calc(100% - 8px)",
        }}
      />
      <div className="grid grid-cols-2 gap-2 relative z-10">
        <button
          role="tab"
          aria-selected={value === "customer"}
          className={`w-full inline-flex items-center justify-center gap-2 rounded-xl py-3 px-3 text-sm transition ${
            value === "customer"
              ? "text-foreground"
              : "text-muted-foreground hover:text-foreground"
          }`}
          onClick={() => onChange("customer")}
          type="button"
        >
          <Phone className="h-4 w-4" />
          <span>Customer</span>
        </button>
        <button
          role="tab"
          aria-selected={value === "staff"}
          className={`w-full inline-flex items-center justify-center gap-2 rounded-xl py-3 px-3 text-sm transition ${
            value === "staff"
              ? "text-foreground"
              : "text-muted-foreground hover:text-foreground"
          }`}
          onClick={() => onChange("staff")}
          type="button"
        >
          <Lock className="h-4 w-4" />
          <span>Staff</span>
        </button>
      </div>
    </div>
  );
}

// IconTextField
function IconTextField(props: {
  id: string;
  type?: string;
  placeholder?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  icon: React.ReactNode;
  autoComplete?: string;
  required?: boolean;
  disabled?: boolean;
}) {
  const {
    id,
    type = "text",
    placeholder,
    value,
    onChange,
    icon,
    autoComplete,
    required,
    disabled,
  } = props;

  return (
    <div className="flex items-center h-12 rounded-xl border border-border bg-background px-3 focus-within:ring-2 focus-within:ring-[hsl(var(--primary)/0.3)] focus-within:border-[hsl(var(--primary))] transition">
      <span className="text-muted-foreground flex-shrink-0 inline-flex items-center justify-center h-5 w-5">
        {icon}
      </span>
      <input
        id={id}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        autoComplete={autoComplete}
        required={required}
        disabled={disabled}
        className="ml-3 w-full bg-transparent outline-none placeholder:text-muted-foreground text-foreground"
      />
    </div>
  );
}

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirect");
  const { refresh } = useAuth();

  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Customer
  const [phone, setPhone] = useState("");
  const [otpStep, setOtpStep] = useState<OtpStep>("phone");
  const [otp, setOtp] = useState("");

  // Staff
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // Tabs
  const [activeTab, setActiveTab] = useState<TabValue>("customer");

  const changeTab = (val: TabValue) => {
    setActiveTab(val);
    setIsPending(false);
    setError(null);
    if (val === "customer") {
      setEmail("");
      setPassword("");
      setOtpStep("phone");
      setOtp("");
    } else {
      setPhone("");
      setOtpStep("phone");
      setOtp("");
    }
  };

  const safeParseJson = async (res: Response) => {
    try {
      return await res.json();
    } catch (err) {
      console.warn("[Login] Failed to parse JSON response", err);
      const text = await res.text().catch(() => "");
      return { _text: text || "" };
    }
  };

  const handleRequestOtp = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    setError(null);
    setIsPending(true);

    const url = `${API_BASE}/api/auth/request-otp`;
    console.log("[Login] request-otp start", { url, phone });

    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ phone }),
      });

      const data = await safeParseJson(res);
      console.log("[Login] request-otp response", { status: res.status, data });

      if (!res.ok) {
        setError((data as any)?.error || "Failed to send OTP");
        return;
      }

      setOtpStep("verify");
    } catch (err) {
      console.error("[Login] request-otp error", err);
      setError("An error occurred. Please try again.");
    } finally {
      setIsPending(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (otp.length !== 6) {
      setError("Please enter the complete 6-digit OTP");
      return;
    }

    setIsPending(true);

    const url = `${API_BASE}/api/auth/verify-otp`;
    console.log("[Login] verify-otp start", { url, phone, otp });

    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ phone, otp }),
      });

      const data = await safeParseJson(res);
      console.log("[Login] verify-otp response", { status: res.status, data });

      if (!res.ok) {
        setError((data as any)?.error || "Invalid OTP");
        return;
      }

      // Refresh auth state to pull user details (name/id/role)
      await refresh();

      // Prefer redirect param; otherwise go to customer dashboard by default
      const next = redirectTo || "/customer/dashboard";
      console.log("[Login] verify-otp navigating", { next });
      navigate(next, { replace: true });
    } catch (err) {
      console.error("[Login] verify-otp error", err);
      setError("An error occurred. Please try again.");
    } finally {
      setIsPending(false);
    }
  };

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsPending(true);

    console.log("[Login] staff-login start", { emailMasked: email.replace(/(.{2}).+(@.*)/, "$1***$2") });

    try {
      const result = await loginWithPassword(email, password);
      console.log("[Login] staff-login result", result);

      if (!result.success) {
        setError(result.error || "Login failed");
        return;
      }

      const next = redirectTo || result.redirectTo || "/staff/dashboard";
      console.log("[Login] staff-login navigating", { next });
      navigate(next, { replace: true });
    } catch (err) {
      console.error("[Login] staff-login error", err);
      setError("An error occurred. Please try again.");
    } finally {
      setIsPending(false);
    }
  };

  const resetOtpFlow = () => {
    console.log("[Login] resetOtpFlow");
    setOtpStep("phone");
    setOtp("");
    setError(null);
  };

  return (
    <div
      className="min-h-screen bg-background"
      style={{
        // Prevent overlap with fixed navbar; uses CSS var set by ZaklynNavbar
        paddingTop:
          "calc(var(--site-header-height, 96px) + env(safe-area-inset-top, 0px) + 16px)",
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.42 }}
        className="w-full max-w-md mx-auto px-4"
      >
        <div className="text-center mb-6">
          <Link to="/" className="inline-block">
            <span className="font-display text-3xl font-semibold text-foreground leading-none">
              {BRAND.name}
            </span>
          </Link>
          <p className="mt-2 text-muted-foreground text-sm max-w-[36rem] mx-auto">
            {BRAND.description}
          </p>
        </div>

        <GlassCard className="p-6 sm:p-8">
          <div className="text-center mb-5">
            <h1 className="font-display text-2xl font-semibold text-foreground">
              Welcome back
            </h1>
            <p className="mt-1 text-muted-foreground text-sm">
              Sign in to manage your orders
            </p>
          </div>

          <SegmentedTabs value={activeTab} onChange={changeTab} />

          <div className="mt-6 min-h-[300px]">
            {activeTab === "customer" ? (
              otpStep === "phone" ? (
                <form onSubmit={handleRequestOtp} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-foreground">
                      Phone Number
                    </Label>
                    <IconTextField
                      id="phone"
                      type="tel"
                      placeholder="Enter your phone number"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      autoComplete="tel"
                      required
                      icon={<Phone className="h-4 w-4" />}
                    />
                    <p className="text-xs text-muted-foreground">
                      We'll send a 6-digit code to verify your number
                    </p>
                  </div>

                  {error && (
                    <div className="p-3 text-sm text-red-600 bg-red-50 rounded-xl border border-red-100">
                      {error}
                    </div>
                  )}

                  <Button
                    type="submit"
                    className="w-full rounded-xl bg-primary hover:bg-primary/90 py-3"
                    disabled={isPending || phone.trim().length < 6}
                  >
                    {isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Sending OTP...
                      </>
                    ) : (
                      "Send OTP"
                    )}
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleVerifyOtp} className="space-y-4">
                  <button
                    type="button"
                    onClick={resetOtpFlow}
                    className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-2"
                  >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Change phone number
                  </button>

                  <div className="space-y-2">
                    <Label className="text-foreground">Enter verification code</Label>
                    <p className="text-sm text-muted-foreground mb-3">
                      We sent a 6-digit code to{" "}
                      <span className="font-medium text-foreground">{phone}</span>
                    </p>

                    {/* OTP aligned horizontally and visible across screen sizes */}
                    <div className="flex justify-center">
                      <InputOTP
                        maxLength={6}
                        value={otp}
                        onChange={(value) => setOtp(value)}
                        aria-label="OTP input"
                        className="w-full max-w-xs"
                      >
                        <InputOTPGroup className="flex items-center justify-between gap-2">
                          <InputOTPSlot
                            index={0}
                            className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg border border-border text-center text-xl sm:text-2xl"
                          />
                          <InputOTPSlot
                            index={1}
                            className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg border border-border text-center text-xl sm:text-2xl"
                          />
                          <InputOTPSlot
                            index={2}
                            className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg border border-border text-center text-xl sm:text-2xl"
                          />
                          <InputOTPSlot
                            index={3}
                            className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg border border-border text-center text-xl sm:text-2xl"
                          />
                          <InputOTPSlot
                            index={4}
                            className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg border border-border text-center text-xl sm:text-2xl"
                          />
                          <InputOTPSlot
                            index={5}
                            className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg border border-border text-center text-xl sm:text-2xl"
                          />
                        </InputOTPGroup>
                      </InputOTP>
                    </div>
                  </div>

                  {error && (
                    <div className="p-3 text-sm text-red-600 bg-red-50 rounded-xl border border-red-100">
                      {error}
                    </div>
                  )}

                  <Button
                    type="submit"
                    className="w-full rounded-xl bg-primary hover:bg-primary/90 py-3"
                    disabled={isPending || otp.length !== 6}
                  >
                    {isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      "Verify & Sign In"
                    )}
                  </Button>

                  <div className="text-center">
                    <button
                      type="button"
                      onClick={handleRequestOtp}
                      disabled={isPending}
                      className="text-sm text-primary hover:text-primary/80 font-medium"
                    >
                      Resend OTP
                    </button>
                  </div>
                </form>
              )
            ) : (
              <form onSubmit={handlePasswordLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-foreground">
                    Email
                  </Label>
                  <IconTextField
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    required
                    icon={<Mail className="h-4 w-4" />}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-foreground">
                    Password
                  </Label>
                  <IconTextField
                    id="password"
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                    icon={<Lock className="h-4 w-4" />}
                  />
                </div>

                {error && (
                  <div className="p-3 text-sm text-red-600 bg-red-50 rounded-xl border border-red-100">
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full rounded-xl bg-foreground hover:bg-foreground/90 text-background py-3"
                  disabled={isPending || email.trim() === "" || password.trim() === ""}
                >
                  {isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    "Sign In"
                  )}
                </Button>
              </form>
            )}
          </div>

          <div className="mt-6 pt-6 border-t border-border">
            <p className="text-xs text-muted-foreground text-center">
              New to {BRAND.name}? Book a measurement appointment to get started with your first order.
            </p>
          </div>
        </GlassCard>

        <p className="mt-6 text-center text-sm text-muted-foreground">
          By signing in, you agree to our{" "}
          <Link to="/terms" className="text-primary hover:underline">
            Terms of Service
          </Link>{" "}
          and{" "}
          <Link to="/privacy" className="text-primary hover:underline">
            Privacy Policy
          </Link>
        </p>
      </motion.div>
    </div>
  );
}